// src/lib/opportunity/calc.ts
//
// Pure pricing-engine helpers for the Opportunity Engine. The UI calls
// these on every keystroke for live totals; the server calls the same
// helpers when persisting a pricing snapshot so the denormalized totals
// match what the user saw. Same code path = "totals you saved == totals
// you printed."
//
// Formula (interpreted from docs/GPT_To_Repo.md Pricing section):
//
//   total_cost        = building_materials + erection + concrete
//                       + engineering + permit_assistance
//                       + sum(additional_items[i].amount)
//   margin_amount     = total_cost * (margin_pct / 100)
//   commission_amount = total_cost * (commission_pct / 100)
//   total_sale_price  = total_cost + margin_amount + commission_amount
//   deposit_amount    = total_sale_price * (deposit_pct / 100)
//   remaining_balance = total_sale_price - deposit_amount
//
// Commission is applied to the total cost basis ("the percentage for
// Bennett's sales organization") rather than to the marked-up sale
// price, matching the user's narrative in GPT_To_Repo.md. A future cut
// can introduce a `commission_basis: 'cost' | 'sale_price'` toggle
// without changing this helper's signature — the engine reads the
// snapshot, the formula gets a branch.
//
// All numbers are rounded to **two decimal places** at every step so a
// list view that sums denormalized totals matches a recomputed total.
// Negative inputs are coerced to 0; non-finite inputs (NaN / Infinity)
// are coerced to 0. The percentage helpers clamp to [0, 100].

// NOTE: this module is imported from both the client (`src/`, where the
// `@/` alias is resolvable) and from server handlers (`server/`, which
// is compiled with the node tsconfig that does *not* declare the alias
// — see `tsconfig.node.json`). Use a relative path so both build
// graphs resolve it cleanly. Same trick is used elsewhere for shared
// pure helpers.
import type {
  OpportunityAdditionalItem,
  OpportunityPricingInputs,
  OpportunityPricingTotals,
} from "../../types/opportunity.js";
import { evaluateSalesAdjustments } from "./salesAdjustmentsEval.js";
import {
  computeGenericDepositAmount,
  subtotalBeforeSalesAdjustments,
  type FormatPresetPricingContext,
} from "./formatPresetPricing.js";

export { round2, safeNumber, safePct } from "../document-builder/formulas/decimalMath.js";
import { round2, safeNumber, safePct } from "../document-builder/formulas/decimalMath.js";

/** Sum of all `additional_items[].amount`, with negative/invalid coerced to 0. */
export function additionalItemsTotal(items: OpportunityAdditionalItem[] | null | undefined): number {
  if (!Array.isArray(items)) return 0;
  let sum = 0;
  for (const item of items) {
    if (!item) continue;
    sum += safeNumber(item.amount);
  }
  return round2(sum);
}

/** Sum of all cost line items + additional items. */
export function totalCost(inputs: Pick<
  OpportunityPricingInputs,
  | "building_materials_cost"
  | "erection_cost"
  | "concrete_cost"
  | "engineering_cost"
  | "permit_assistance_cost"
  | "additional_items"
>): number {
  const sum =
    safeNumber(inputs.building_materials_cost) +
    safeNumber(inputs.erection_cost) +
    safeNumber(inputs.concrete_cost) +
    safeNumber(inputs.engineering_cost) +
    safeNumber(inputs.permit_assistance_cost) +
    additionalItemsTotal(inputs.additional_items);
  return round2(sum);
}

/** Margin amount = total_cost * margin_pct / 100. */
export function marginAmount(totalCostValue: number, marginPct: number): number {
  return round2(safeNumber(totalCostValue) * (safePct(marginPct) / 100));
}

/** Commission amount = total_cost * commission_pct / 100 (commission on cost basis). */
export function commissionAmount(totalCostValue: number, commissionPct: number): number {
  return round2(safeNumber(totalCostValue) * (safePct(commissionPct) / 100));
}

/** Total sale price = cost + margin + commission. */
export function totalSalePrice(args: {
  totalCost: number;
  marginAmount: number;
  commissionAmount: number;
}): number {
  return round2(
    safeNumber(args.totalCost) + safeNumber(args.marginAmount) + safeNumber(args.commissionAmount),
  );
}

/** Deposit amount = total_sale_price * deposit_pct / 100. */
export function depositAmount(totalSalePriceValue: number, depositPct: number): number {
  return round2(safeNumber(totalSalePriceValue) * (safePct(depositPct) / 100));
}

/** Remaining balance = total_sale_price - deposit_amount (clamped to ≥ 0). */
export function remainingBalance(totalSalePriceValue: number, depositAmountValue: number): number {
  const remaining = safeNumber(totalSalePriceValue) - safeNumber(depositAmountValue);
  return round2(remaining < 0 ? 0 : remaining);
}

/**
 * Generic-format totals. Subtotal is the single rep-entered figure;
 * tax + deposit each pick up a `$` / `%` mode from the inputs. Margin
 * and commission are *not* computed for generic (the generic format is
 * customer-price-only, no cost-basis math — the snapshot stores those
 * fields as 0 for parity with red_iron rows).
 */
export function computeGenericPricingTotals(
  inputs: OpportunityPricingInputs,
  formatPreset?: FormatPresetPricingContext | null,
): OpportunityPricingTotals {
  const { formatEval, amount: preAdjustmentSubtotal } = subtotalBeforeSalesAdjustments({
    inputs,
    preset: formatPreset,
  });
  const subtotalEval = evaluateSalesAdjustments(
    preAdjustmentSubtotal,
    inputs.sales_adjustments,
  );
  const subtotal = subtotalEval.adjustedAmount;
  const tax_amount = round2(
    inputs.tax_mode === "percent"
      ? subtotal * (safePct(inputs.tax_value) / 100)
      : safeNumber(inputs.tax_value),
  );
  const total_sale_price = round2(subtotal + tax_amount);
  const deposit_amount = computeGenericDepositAmount({
    inputs,
    totalSalePrice: total_sale_price,
    formatEval,
  });
  const remaining_balance = round2(Math.max(0, total_sale_price - deposit_amount));
  return {
    // Generic format reports the subtotal as the "total cost" for
    // downstream consumers that look for a single cost figure. No
    // margin/commission math.
    total_cost: subtotal,
    margin_amount: 0,
    commission_amount: 0,
    total_sale_price,
    deposit_amount,
    remaining_balance,
    tax_amount,
  };
}

/** All-at-once: compute every derived total from a full input set. */
export function computePricingTotals(
  inputs: OpportunityPricingInputs,
  options?: { formatPreset?: FormatPresetPricingContext | null },
): OpportunityPricingTotals {
  if (inputs.format_key === "generic") {
    return computeGenericPricingTotals(inputs, options?.formatPreset);
  }
  const baseCost = totalCost(inputs);
  const costEval = evaluateSalesAdjustments(baseCost, inputs.sales_adjustments);
  const total_cost = costEval.adjustedAmount;
  const margin_amount = marginAmount(total_cost, inputs.margin_pct);
  const commission_amount = commissionAmount(total_cost, inputs.commission_pct);
  const total_sale_price = totalSalePrice({
    totalCost: total_cost,
    marginAmount: margin_amount,
    commissionAmount: commission_amount,
  });
  const deposit_amount = depositAmount(total_sale_price, inputs.deposit_pct);
  const remaining_balance = remainingBalance(total_sale_price, deposit_amount);
  return {
    total_cost,
    margin_amount,
    commission_amount,
    total_sale_price,
    deposit_amount,
    remaining_balance,
    tax_amount: 0,
  };
}

/** Empty / default inputs used when creating a new opportunity. */
export function emptyPricingInputs(
  defaults: Partial<
    Pick<OpportunityPricingInputs, "commission_pct" | "margin_pct" | "deposit_pct" | "format_key">
  > = {},
): OpportunityPricingInputs {
  return {
    format_key: defaults.format_key ?? "red_iron",
    building_materials_cost: 0,
    erection_cost: 0,
    concrete_cost: 0,
    engineering_cost: 0,
    permit_assistance_cost: 0,
    additional_items: [],
    commission_pct: safePct(defaults.commission_pct ?? 0),
    margin_pct: safePct(defaults.margin_pct ?? 0),
    deposit_pct: safePct(defaults.deposit_pct ?? 25),
    subtotal_input: 0,
    tax_mode: "percent",
    tax_value: 0,
    deposit_mode: "percent",
    deposit_amount_input: 0,
    sales_adjustments: [],
    deposit_deviation_note: null,
    format_calc_values: {},
    format_calc_modes: {},
    format_calc_deviation_notes: {},
  };
}
